import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import NeoOTP from '../../../components/FormComponents/NeoOTP'
import NeoSuccess from '../../../components/FormComponents/NeoSuccess'
import LayoutWithAccountCard from '../../../layout/Layout/layout-account-card'
import NeoButton from '../../../components/NeoButton/NeoButton'
import { carouselCurrentItemSelector } from '../../../selectors'
import NeoInput from '../../../components/NeoInput/NeoInput'
import InputError from '../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import AccountDisplay from '../../../components/account-display/account-display'
import CardLessService from '../../../services/cardless.service'
import { CardLessSchema } from '../../../schemas/send-to-mobile'
import TermsAndCondition from '../../../components/TermsAndConditions/TermsAndCondition'
import config from 'custom/config/environment'
import { useTranslation } from 'react-i18next'
import {
  CARDLESS_CASH_KEYS,
  SIDENAV_KEYS,
  VALIDATION_MESSAGES,
  RECIPTS_KEYS,
} from '../../../utils/translation-constants'
import AddIcon from 'custom/assets/common-icons/Add.svg'
import GimacService from '../../../services/gimac.service'
import Pagination from 'react-js-pagination'
import moment from 'moment'
import validationConfig from 'custom/config/validation.config.json'
import NumberFormat from 'react-number-format'
import { DownloadReceiptSchema } from '../../../schemas/others/downloadCenter/downloadReceipt'
import { setAccRefresh } from '../../../actions'

const downloadReceipConfig = validationConfig['downloadCenter']['downloadReceipt']

export default function GimacAtm() {
  const history = useHistory()
  const dispatch = useDispatch()
  const [stage, setStage] = useState(0)
  const [loader, setLoader] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [verifyDetails, setVerifyDetails] = useState({})
  const [otp, setOtp] = useState('')
  const { t } = useTranslation()
  const [accountList, setAccountList] = useState([])
  const [formValues, setFormValues] = useState({
    remarks: '',
    mobileNumber: '',
    expiry: '',
    transferAmount: '',
    maxTransferAmount: undefined,
  })
  const currentAccountDetail = useSelector(carouselCurrentItemSelector)
  const [tokenList, setTokenList] = useState([])
  const [noRecords, setNoRecords] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageLimit = 10
  const [totalRecords, setTotalRecords] = useState(10)
  const [expanded, setExpanded] = useState(null)

  const cardLessViewDetails = useCallback(
    async (page, values) => {
      try {
        setLoader(true)
        setExpanded(null)
        let reqParams = {
          page: page,
          size: pageLimit,
          fromDate: values?.fromDate || '',
          toDate: values?.toDate || '',
        }
        const response = await GimacService.cardlessTransactionList(reqParams)
        if (response.status === 200) {
          setLoader(false)
          setTokenList(response.data['cardlessReceiptDetails'])
          setNoRecords(response.data['cardlessReceiptDetails'].length === 0 ? true : false)
          setTotalRecords(response.data['totalItems'])
        }
      } catch (err) {
        setLoader(false)
      }
    },
    [currentAccountDetail, status],
  )

  const paginationChange = useCallback(async (page) => {
    setCurrentPage(page)
    cardLessViewDetails(page - 1)
  }, [])

  useEffect(() => {
    cardLessViewDetails(0)
  }, [])

  const onVerify = useCallback(
    async (values) => {
      try {
        if (values.transferAmount === '0.00' || values.transferAmount === '.00') {
          return
        }
        if (values.expiry === '0') {
          return
        }
        setLoading(true)
        const req = {
          amount: Number(parseFloat(values.transferAmount.replaceAll(',', '')).toFixed(2)),
          mobileNumber: values.mobileNumber,
          expiry: values.expiry,
          srcAccount: currentAccountDetail.accountNo,
          srcAccBranch: currentAccountDetail.brnCode,
          srcAccCcy: currentAccountDetail.currency,
          txnCurrency: currentAccountDetail.currency,
          remarks: values.remarks,
        }
        setFormValues(values)
        const response = await CardLessService.cardlessGenTokenVerify(req)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setLoading(false)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [currentAccountDetail],
  )

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const req = {
        amount: Number(parseFloat(formValues.transferAmount.replaceAll(',', '')).toFixed(2)),
        mobileNumber: formValues.mobileNumber,
        expiry: formValues.expiry,
        srcAccount: currentAccountDetail.accountNo,
        srcAccBranch: currentAccountDetail.brnCode,
        srcAccCcy: currentAccountDetail.currency,
        txnCurrency: currentAccountDetail.currency,
        remarks: formValues.remarks,
        otp: otp,
      }
      const response = await CardLessService.cardlessGenToken(req)
      if (response.status === 200) {
        setLoading(false)
        dispatch(setAccRefresh(true))
        setMessage(response.data['message'])
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [currentAccountDetail, formValues, otp])

  const fetchAccounts = useCallback(async () => {
    const x = await CardLessService.getAllAccounts()
    setAccountList(x.accountSummaryList)
    setStage(0)
  })
  
  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderCardless()
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} onClick={() => {
          setStage(0)
          cardLessViewDetails()
          fetchAccounts()
          setFormValues({})
        }} />
      default:
        return renderCardless()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  const onSearch = useCallback(async (values) => {
    if (values) {
      setFormValues({
        fromDate: values?.fromDate,
        toDate: values?.toDate,
      })
    }
    await cardLessViewDetails(0, values)
  }, [])

  const getDownload = useCallback(async (selected) => {
    try {
      setLoading(true)
      const reqParams = {
        transRefNum: selected.txnRefNo,
        id: selected.id,
      }
      await GimacService.cardlessDownLoadReport(reqParams)
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }, [])

  function renderCardless() {
    return (
      <>
        <div className='row '>
          <div className='col-7 '>
            <div className='heading-style' style={{ margin: 0, lineHeight: '40px' }}>
              GIMAC ATM Provision
            </div>
          </div>
          <div className='col-5 text-right'>
            <button
              onClick={() => setStage(1)}
              type='button'
              className='btn detailed-statement-btn'>
              <img width={25} height={25} src={AddIcon} />
              <span style={{ marginLeft: 10 }} className='font-weight-600'>
                {t(CARDLESS_CASH_KEYS.GENERATE_TOKEN)}
              </span>
            </button>
          </div>
        </div>
        <>
          <Formik
            initialValues={formValues}
            validationSchema={DownloadReceiptSchema}
            onSubmit={onSearch}>
            {({ isSubmitting, errors, touched, handleChange, values }) => (
              <Form>
                <div className='row'>
                  <div className='form-group col-4 p-0 mx-3'>
                    <NeoInput
                      showAsterisk={downloadReceipConfig['isFromDateRequired']}
                      text={t(RECIPTS_KEYS.FORM_DATE)}
                      name='fromDate'
                      type='date'
                      onChange={handleChange}
                      max={moment(new Date()).format('YYYY-MM-DD')}
                    //value={values.fromDate}
                    />
                    <InputError text={errors.fromDate} touched={touched.fromDate} />
                  </div>
                  <div className='form-group col-4 p-0 mx-3'>
                    <NeoInput
                      showAsterisk={downloadReceipConfig['isToDateRequired']}
                      text={t(RECIPTS_KEYS.TO_DATE)}
                      name='toDate'
                      type='date'
                      onChange={handleChange}
                      max={moment(new Date()).format('YYYY-MM-DD')}
                    //value={values.toDate}
                    />
                    <InputError text={errors.toDate} touched={touched.toDate} />
                  </div>
                  <div className='col-2 p-0' style={{ marginTop: '30px' }}>
                    <NeoButton type='submit' text={t(RECIPTS_KEYS.SEARCH)} mode='newDesign' />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
          {noRecords && <div className='no-data-found mt-4'>{t(CARDLESS_CASH_KEYS.NO_RECORDS)} </div>}

          {tokenList.map((option, index) => (
            <>
              <div key={index} className='approval-card mt-4'>
                <div
                  className='approval-card-header'
                  onClick={async () => {
                    if (expanded == index) {
                      setExpanded(null)
                    } else {
                      setExpanded(index)
                    }
                  }}>
                  <span>
                    <span>
                      {'Source Account No'} : {option.sourceAccountNo}
                    </span>
                    <span style={{ marginTop: 5 }}>
                      {' '}
                      {t(CARDLESS_CASH_KEYS.REFERENCE)} : {option.txnRefNo}
                    </span>
                  </span>

                  <span>
                    <span>
                      {t(CARDLESS_CASH_KEYS.EXPIRY_DATE)} :{' '}
                      {moment(option?.expiry).format('DD-MM-YYYY h:mm A')}
                    </span>
                    <span style={{ marginTop: 5 }}>
                      {' '}
                      {t(CARDLESS_CASH_KEYS.STATUS)} : {option.status}
                    </span>
                  </span>
                </div>
                {expanded === index && (
                  <div className='approval-card-body'>
                    <div className='row'>
                      <div className='col-4'>
                        <label>{'Source Account No'}</label>
                        <span>{option?.sourceAccountNo || '-'}</span>
                      </div>
                      <div className='col-4'>
                        <label>{t(CARDLESS_CASH_KEYS.MOBILE_NO)}</label>
                        <span>{option?.mobileNumber || '-'}</span>
                      </div>
                      <div className='col-4'>
                        <label>{'Currency'}</label>
                        <span>{option?.currency || '-'}</span>
                      </div>
                      <div className='col-4'>
                        <label>{t(CARDLESS_CASH_KEYS.AMOUNT)}</label>
                        {/* <span>{option?.amount || '-'}</span> */}
                        <span className=''>
                          <NumberFormat
                            value={option?.amount}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={''}
                            className='d-inline'
                            fixedDecimalScale={true}
                            decimalScale={2}
                            allowEmptyFormatting
                          />
                        </span>
                      </div>

                      <div className='col-4'>
                        <label>{t(CARDLESS_CASH_KEYS.CREATE_DATE)}</label>
                        <span>{moment(option?.createdDate).format('DD-MM-YYYY h:mm A')}</span>
                      </div>

                      <div className='col-4'>
                        <label>{t(CARDLESS_CASH_KEYS.EXPIRY_DATE)}</label>
                        <span>{moment(option?.expiry).format('DD-MM-YYYY h:mm A')}</span>
                      </div>
                      <div className='col-4'>
                        <label>{t(CARDLESS_CASH_KEYS.STATUS)}</label>
                        <span>{option.status}</span>
                      </div>
                    </div>

                    <div class='row'>
                      <h6
                        className='download-link'
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          getDownload(option)
                        }}>
                        {t(RECIPTS_KEYS.DOWNLOAD)}
                      </h6>
                    </div>

                    {/* {menuData['CARDLESS_CASH']['CANCEL_TOKEN'] && (
                    <>
                      {(option.status === 'PENDING' || option.status === 'EXPIRED') && (
                        <div className='d-flex' style={{ marginTop: 20, justifyContent: 'center' }}>
                          {loading ? (
                            renderLoading()
                          ) : (
                            <a onClick={() => handleCancelToken(option)}>
                              {t(CARDLESS_CASH_KEYS.CANCEL_TOKEN)}
                            </a>
                          )}
                        </div>
                      )}
                    </>
                  )} */}
                  </div>
                )}
              </div>
            </>
          ))}
          <div className='d-flex justify-content-center mb-5 mt-2'>
            {!loading && tokenList?.length > 0 && tokenList?.length > 5 && (
              <Pagination
                activePage={currentPage}
                itemsCountPerPage={pageLimit}
                totalItemsCount={totalRecords}
                pageRangeDisplayed={5}
                itemClass='page-item'
                linkClass='page-link'
                onChange={paginationChange}
              />
            )}
          </div>
        </>
      </>
    )
  }

  function renderForm() {
    return (
      <div className='new-design-form-style'>
        <div className='heading-style'>GIMAC ATM Provision</div>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={CardLessSchema}
          onSubmit={onVerify}>
          {({ isSubmitting, handleChange, errors, values, touched, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    text={t(CARDLESS_CASH_KEYS.RECEIVER_MOBILE_NO)}
                    onChange={(value) => {
                      setFieldValue('mobileNumber', value?.value)
                    }}
                    mode='onlyNumber'
                    name='mobileNumber'
                  />

                  <InputError text={errors.mobileNumber} touched={touched.mobileNumber} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    text={t(CARDLESS_CASH_KEYS.AMOUNT)}
                    name='amount'
                    onChange={(values) => {
                      console.log(values)
                      setFieldValue('transferAmount', values.value)
                      setFieldValue('maxTransferAmount', values.floatValue)
                    }}
                    mode='amount'
                  />
                  <InputError text={errors.transferAmount} touched={touched.transferAmount} />
                  <InputError
                    text={t(CARDLESS_CASH_KEYS.AMOUNT_VALIDITY)}
                    touched={values.maxTransferAmount < 0}
                  />
                  <InputError
                    text={t(VALIDATION_MESSAGES.AMOUNT_VALID)}
                    touched={values.transferAmount === '0.00' || values.transferAmount === '.00'}
                  />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    text={t(CARDLESS_CASH_KEYS.VALIDITY_HRS)}
                    onChange={(value) => {
                      setFieldValue('expiry', value?.value)
                    }}
                    mode='onlyNumber'
                    name='expiry'
                    minLength={1}
                    maxLength={2}
                  />
                  <InputError text={errors.expiry} touched={touched.expiry} />
                  <InputError
                    text={t(VALIDATION_MESSAGES.VALIDITY_VALUE)}
                    touched={values.expiry === '0'}
                  />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='4'
                    text={t(CARDLESS_CASH_KEYS.REMARKS)}
                    onChange={handleChange}
                    name='remarks'
                  />
                  <InputError text={errors.remarks} touched={touched.remarks} />
                </div>
              </div>
              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex='5'
                    type='submit'
                    mode='newDesign'
                    text={t(SIDENAV_KEYS.TOUCH_ID_KEYS.SUBMIT)}
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  tabIndex={'5'}
                  onClick={() => setStage(0)}>
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='new-design-form-style'>
        <div className='d-flex flex-column confirmation'>
          <h3>Confirm Gimac ATM Provision</h3>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.RECEIVER_MOBILE_NO)}</h6>
            <h6 className='value'>{`${config.COUNTRY_CODE}${formValues?.mobileNumber}`}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.AMOUNT)}</h6>
            <h6 className='value'>{`${currentAccountDetail?.currency} ${formValues?.amount}`}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.CHARGES)}</h6>
            <h6 className='value'>{verifyDetails?.charges}</h6>
          </div>

          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.VALIDATIY)}</h6>
            <h6 className='value'>{formValues?.expiry} hrs</h6>
          </div>
          {/* <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.TRANSACTION_CURRENCY)}</h6>
            <h6 className='value'>{currentAccountDetail?.currency}</h6>
          </div> */}

          <div>
            <h6 className='label'>{t(CARDLESS_CASH_KEYS.REMARKS)}</h6>
            <h6 className='value'>{formValues?.remarks}</h6>
          </div>

          <div className='form-group col-12'>
            <TermsAndCondition module='CARDLESSCASHOUT' />
          </div>

          <div className='mt-3'>
            {loading ? (
              renderLoading()
            ) : (
              <NeoButton
                onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
                type='button'
                mode='newDesign'
                text={t(SIDENAV_KEYS.TOUCH_ID_KEYS.CONFIRM)}
              />
            )}
          </div>
          <div className='back-cancel-style' onClick={() => history.goBack()}>
            {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
          </div>
        </div>
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='GIMAC_ATM'>
      <div>{renderSwitch()}</div>
    </LayoutWithAccountCard>
  )
}
